
import { defineComponent, ref, onMounted, onUnmounted } from 'vue'
import SpinnerLoader from '@/components/SpinnerLoader.vue'
import api from '@/api/api'
import QRCode from 'qrcode'

/**
 * Провайдер зашит в расшаренную ссылку в момент её создания и живёт вечно: токен — это
 * просто base64 от connectorId, он не протухает. До 26.09 ссылки строились по сырому
 * провайдеру, поэтому старые ведут на сторонние ручки. 05.10 по такой ссылке клиенту
 * показали QR свежесозданного w-api инстанса, он его отсканил, и канал уехал обратно к
 * стороннему (CVLXBVUG6_telegramChannel_ore, инстанс 10782). Поэтому ссылке больше не
 * верим и ведём на наш провайдер — как это давно делает само приложение.
 */
const LEGACY_TO_DH: Record<string, string> = {
    'wApi-telegram': 'dh-telegram',
    'basis-api': 'dh-whatsapp',
    'wApi-whatsapp': 'dh-whatsapp',
    'green-api-max': 'dh-max',
}

export default defineComponent({
  name: 'QrCodePage',
  components: {
    SpinnerLoader
  },
  setup() {
    const qrCode = ref('')
    const updateInterval = ref<any>()
    const tokenError = ref<any>(false)
    const selectedBotProvider = ref('')
    const lastTgQrRequestTime = ref<number | null>(null) // Время последнего запроса QR-кода для Telegram
    const lastMaxQrRequestTime = ref<number | null>(null) // Время последнего запроса QR-кода для MAX
    const lastWa2QrRequestTime = ref<number | null>(null) // Время последнего запроса QR-кода для WhatsApp 2

    const updateQrCode = async () => {
      const searchParams = new URLSearchParams(window.location.search)
      const qrToken = searchParams.get('token')
      const isChannelsRework = searchParams.get('isChannelsRework') === 'true'
      const linkProvider = searchParams.get('provider')

      const provider = LEGACY_TO_DH[linkProvider] || linkProvider

      // Определяем selectedBotProvider из query параметра
      if (provider) {
        selectedBotProvider.value = provider
      }

      if (!qrToken && !tokenError.value) {
        tokenError.value = true
      }

      let response
      if (isChannelsRework && provider === 'dh-max') {
        response = await api.networkFixer.getNoAuthDhMaxQrRework({ qrToken })
      } else if (isChannelsRework && provider === 'green-api-max') {
        // MAX в режиме каналов
        const now = Date.now()
        // Проверяем, прошло ли более 15 секунд с последнего запроса
        if (lastMaxQrRequestTime.value !== null && (now - lastMaxQrRequestTime.value) < 15000) {
          // Пропускаем запрос, если прошло меньше 15 секунд
          return
        }
        
        response = await api.networkFixer.getNoAuthMaxBasisQrRework({ qrToken })
        
        // Сохраняем время текущего запроса ПОСЛЕ выполнения запроса
        lastMaxQrRequestTime.value = Date.now()
      } else if (isChannelsRework && provider === 'wApi-telegram') {
        // Telegram 1 в режиме каналов
        const now = Date.now()
        // Проверяем, прошло ли более 35 секунд с последнего запроса
        if (lastTgQrRequestTime.value !== null && (now - lastTgQrRequestTime.value) < 35000) {
          // Пропускаем запрос, если прошло меньше 35 секунд
          return
        }
        
        response = await api.networkFixer.getNoAuthTgWaQrRework({ qrToken })
        
        // Сохраняем время текущего запроса ПОСЛЕ выполнения запроса
        lastTgQrRequestTime.value = Date.now()
      } else if (isChannelsRework && provider === 'wApi-whatsapp') {
        // WhatsApp 2 в режиме каналов
        const now = Date.now()
        // Проверяем, прошло ли более 10 секунд с последнего запроса
        if (lastWa2QrRequestTime.value !== null && (now - lastWa2QrRequestTime.value) < 10000) {
          // Пропускаем запрос, если прошло меньше 10 секунд
          return
        }
        
        response = await api.networkFixer.getNoAuthWApiQrRework({ qrToken })
        
        // Сохраняем время текущего запроса ПОСЛЕ выполнения запроса
        lastWa2QrRequestTime.value = Date.now()
      } else if (isChannelsRework && provider === 'dh-telegram') {
        // Telegram 2 (dh-telegram) в режиме каналов
        response = await api.networkFixer.getNoAuthTgDhQrRework({ qrToken })
      } else if (isChannelsRework && provider === 'dh-whatsapp') {
        // WhatsApp 3 (dh-whatsapp) в режиме каналов
        response = await api.networkFixer.getNoAuthWaDhQrRework({ qrToken })
      } else if (isChannelsRework) {
        // WhatsApp Basis в режиме каналов
        response = await api.networkFixer.getNoAuthWaBasisQrRework({ qrToken })
      } else if (provider === 'green-api-max') {
        // MAX без isChannelsRework (старый режим)
        const now = Date.now()
        // Проверяем, прошло ли более 15 секунд с последнего запроса
        if (lastMaxQrRequestTime.value !== null && (now - lastMaxQrRequestTime.value) < 15000) {
          // Пропускаем запрос, если прошло меньше 15 секунд
          return
        }

        response = await api.networkFixer.getNoAuthMaxQr({ qrToken })
        
        // Сохраняем время текущего запроса ПОСЛЕ выполнения запроса
        lastMaxQrRequestTime.value = Date.now()
      } else if (provider === 'wApi-telegram') {
        // Telegram 1 без isChannelsRework
        const now = Date.now()
        // Проверяем, прошло ли более 35 секунд с последнего запроса
        if (lastTgQrRequestTime.value !== null && (now - lastTgQrRequestTime.value) < 35000) {
          // Пропускаем запрос, если прошло меньше 35 секунд
          return
        }

        response = await api.networkFixer.getNoAuthTgWapiQr({ qrToken })
        
        // Сохраняем время текущего запроса ПОСЛЕ выполнения запроса
        lastTgQrRequestTime.value = Date.now()
      } else if (provider === 'wApi-whatsapp') {
        // WhatsApp 2 без isChannelsRework
        const now = Date.now()
        // Проверяем, прошло ли более 10 секунд с последнего запроса
        if (lastWa2QrRequestTime.value !== null && (now - lastWa2QrRequestTime.value) < 10000) {
          // Пропускаем запрос, если прошло меньше 10 секунд
          return
        }

        response = await api.networkFixer.getNoAuthWaWapiQr({ qrToken })
        
        // Сохраняем время текущего запроса ПОСЛЕ выполнения запроса
        lastWa2QrRequestTime.value = Date.now()
      } else {
        // Старый режим для остальных провайдеров
        response = await api.networkFixer.getNoAuthQr({ qrToken })
      }

      // Канал, закреплённый за сторонним рычагом поддержки, наша ручка не обслуживает —
      // отвечает «channel is pinned to ...». Для таких возвращаемся к старой ручке, чтобы
      // расшаренная ссылка у них продолжала работать как раньше.
      if (
        provider !== linkProvider &&
        String(response.data?.message || '').includes('pinned')
      ) {
        if (linkProvider === 'wApi-telegram') {
          response = await api.networkFixer.getNoAuthTgWaQrRework({ qrToken })
        } else if (linkProvider === 'green-api-max') {
          response = await api.networkFixer.getNoAuthMaxBasisQrRework({ qrToken })
        } else if (linkProvider === 'wApi-whatsapp') {
          response = await api.networkFixer.getNoAuthWApiQrRework({ qrToken })
        } else if (linkProvider === 'basis-api') {
          response = await api.networkFixer.getNoAuthWaBasisQrRework({ qrToken })
        }
      }

      if (response.error?.code === 500 && qrCode.value) {
        qrCode.value = ''
      }
      if (response.error?.code === 400 && !tokenError.value) {
        tokenError.value = true
      }
      if (response.data?.status === 'ready' && response.data?.qr) {
        if (tokenError.value) {
          tokenError.value = false
        }
        if (provider === 'dh-whatsapp') {
          // wa-service отдаёт QR СЫРОЙ строкой Baileys (не base64 PNG) — рендерим через canvas.
          const canvas = document.createElement('canvas')
          await QRCode.toCanvas(canvas, response.data.qr)
          qrCode.value = canvas.toDataURL('image/png')
        } else {
          qrCode.value = 'data:image/png;base64,' + response.data.qr
        }
      } else if (response.data?.type === 'qrCode' || response.data?.type === 'qrcode' || response.data?.data?.qr) {
        if (tokenError.value) {
          tokenError.value = false
        }
        if(response.data?.data?.qr) {
          qrCode.value = 'data:image/jpg;base64,' + response.data?.data?.qr
        } else if (response.data?.message?.includes('data:image/jpg;base64,') || response.data?.message?.includes('data:image/png;base64,')) {
          qrCode.value = response.data?.message
        } else {
          qrCode.value = 'data:image/jpg;base64,' + response.data?.message
        }        
      }
      if (
        response.data?.type === 'alreadyLogged' ||
        response.data?.status === 'authorized' ||
        response.data?.stateInstance === 'authorized' ||
        response.data === 'authorized'
      ) {
        if (tokenError.value) {
          tokenError.value = false
        }
        qrCode.value = 'alreadyLogged'
        // Сбрасываем таймеры при авторизации
        lastTgQrRequestTime.value = null
        lastMaxQrRequestTime.value = null
        lastWa2QrRequestTime.value = null
      }
    }

    onMounted(async () => {
      // Инициализируем selectedBotProvider из query параметра. Старые ссылки несут
      // сторонний провайдер — приводим к нашему, см. LEGACY_TO_DH в updateQrCode.
      const searchParams = new URLSearchParams(window.location.search)
      const linkProvider = searchParams.get('provider')
      const provider = LEGACY_TO_DH[linkProvider] || linkProvider
      if (provider) {
        selectedBotProvider.value = provider
      }
      
      updateQrCode()
      updateInterval.value = setInterval(updateQrCode, 5000)
    })

    onUnmounted(() => {
      if (updateInterval.value) {
        clearInterval(updateInterval.value)
      }
    })

    return {
      qrCode,
      tokenError,
      selectedBotProvider
    }
  }
})
